import React, { useState } from 'react';
import { useTeamStore, useSubscriptionStore, useAuthStore, useUIStore } from '../stores';
import { Link } from 'react-router-dom';
import {
  Users,
  UserPlus,
  Mail,
  Shield,
  Crown,
  MoreHorizontal,
  Trash2,
  Edit,
  Check,
  X,
  Loader2,
  Lock,
  Sparkles,
} from 'lucide-react';

const roleColors = {
  owner: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
  admin: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
  editor: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  viewer: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400',
};

const roleDescriptions = {
  admin: 'Full access to all features and settings',
  editor: 'Can create and schedule posts',
  viewer: 'Can view analytics and scheduled posts',
};

export default function Team() {
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('editor');
  const [isInviting, setIsInviting] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  
  const { members, pendingInvites, inviteMember, removeMember, updateMemberRole } = useTeamStore();
  const { plan: subPlan, getCurrentFeatures } = useSubscriptionStore();
  const { user } = useAuthStore();
  const { addToast } = useUIStore();
  const plan = user?.plan || subPlan || 'free';
  
  const allFeatures = useSubscriptionStore.getState().features;
  const features = allFeatures[plan] || allFeatures.free;
  const maxMembers = features.teamMembers;
  const canAddMembers = maxMembers === -1 || members.length < maxMembers;
  const isTeamFeatureAvailable = maxMembers > 0 || maxMembers === -1;
  
  const handleInvite = async () => {
    if (!inviteEmail) {
      addToast({ type: 'error', title: 'Email required', message: 'Please enter an email address.' });
      return;
    }
    
    setIsInviting(true);
    const result = await inviteMember(inviteEmail, inviteRole);
    
    if (result.success) {
      addToast({
        type: 'success',
        title: 'Invitation sent!',
        message: `Invite sent to ${inviteEmail}`,
      });
      setInviteEmail('');
      setShowInviteModal(false);
    }
    
    setIsInviting(false);
  };
  
  const handleRemove = async (memberId) => {
    const result = await removeMember(memberId);
    if (result.success) {
      addToast({ type: 'info', title: 'Member removed', message: 'Team member has been removed.' });
    }
  };
  
  const handleRoleChange = async (memberId, newRole) => {
    await updateMemberRole(memberId, newRole);
    setEditingMember(null);
    addToast({ type: 'success', title: 'Role updated', message: 'Team member role has been updated.' });
  };
  
  if (!isTeamFeatureAvailable) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Team</h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            Collaborate with your team
          </p>
        </div>
        
        <div className="card p-12 text-center">
          <div className="w-16 h-16 rounded-full bg-brand-100 dark:bg-brand-900/30 flex items-center justify-center mx-auto mb-4">
            <Lock className="w-8 h-8 text-brand-600 dark:text-brand-400" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            Team collaboration is a Pro feature
          </h2>
          <p className="text-gray-500 dark:text-gray-400 max-w-md mx-auto mb-6">
            Upgrade to Pro to invite team members, assign roles, and collaborate on your social media content.
          </p>
          <Link to="/billing" className="btn-primary">
            <Sparkles className="w-4 h-4 mr-2" />
            Upgrade to Pro
          </Link>
        </div>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Team</h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            Manage your team members and permissions
          </p>
        </div>
        
        <button 
          onClick={() => setShowInviteModal(true)}
          disabled={!canAddMembers}
          className="btn-primary"
        >
          <UserPlus className="w-4 h-4 mr-2" />
          Invite Member
        </button>
      </div>
      
      {/* Team stats */}
      <div className="card p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-brand-100 dark:bg-brand-900/30 flex items-center justify-center">
            <Users className="w-5 h-5 text-brand-600 dark:text-brand-400" />
          </div>
          <div>
            <p className="font-medium text-gray-900 dark:text-white">
              {members.length} of {maxMembers === -1 ? '∞' : maxMembers} team members
            </p>
            <p className="text-sm text-gray-500">
              {pendingInvites.length} pending invite{pendingInvites.length !== 1 ? 's' : ''}
            </p>
          </div>
        </div>
      </div>
      
      {/* Members list */}
      <div className="card">
        <div className="p-4 border-b border-gray-200 dark:border-gray-800">
          <h3 className="font-semibold text-gray-900 dark:text-white">Team Members</h3>
        </div>
        <div className="divide-y divide-gray-200 dark:divide-gray-800">
          {members.map((member) => (
            <div key={member.id} className="p-4 flex items-center gap-4">
              <img 
                src={member.avatar} 
                alt={member.name}
                className="w-10 h-10 rounded-full"
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="font-medium text-gray-900 dark:text-white">{member.name}</p>
                  {member.role === 'owner' && (
                    <Crown className="w-4 h-4 text-yellow-500" />
                  )}
                </div>
                <p className="text-sm text-gray-500">{member.email}</p>
              </div>
              
              {editingMember === member.id ? (
                <div className="flex items-center gap-2">
                  <select
                    defaultValue={member.role}
                    onChange={(e) => handleRoleChange(member.id, e.target.value)}
                    className="input py-1.5 text-sm w-auto"
                  >
                    <option value="admin">Admin</option>
                    <option value="editor">Editor</option>
                    <option value="viewer">Viewer</option>
                  </select>
                  <button 
                    onClick={() => setEditingMember(null)}
                    className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-3">
                  <span className={`text-xs font-medium px-2 py-1 rounded-full capitalize ${roleColors[member.role]}`}>
                    {member.role}
                  </span>
                  
                  {member.role !== 'owner' && (
                    <div className="flex items-center gap-1">
                      <button 
                        onClick={() => setEditingMember(member.id)}
                        className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button 
                        onClick={() => handleRemove(member.id)}
                        className="p-1.5 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/20 text-gray-400 hover:text-red-500"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      
      {/* Pending invites */}
      {pendingInvites.length > 0 && (
        <div className="card">
          <div className="p-4 border-b border-gray-200 dark:border-gray-800">
            <h3 className="font-semibold text-gray-900 dark:text-white">Pending Invitations</h3>
          </div>
          <div className="divide-y divide-gray-200 dark:divide-gray-800">
            {pendingInvites.map((invite) => (
              <div key={invite.id} className="p-4 flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                  <Mail className="w-5 h-5 text-gray-400" />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-gray-900 dark:text-white">{invite.email}</p>
                  <p className="text-sm text-gray-500">Invited as {invite.role}</p>
                </div>
                <span className="text-xs text-yellow-600 dark:text-yellow-400 bg-yellow-100 dark:bg-yellow-900/30 px-2 py-1 rounded-full">
                  Pending
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Invite Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="card w-full max-w-md p-6 animate-fade-in">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Invite Team Member</h3>
              <button 
                onClick={() => setShowInviteModal(false)}
                className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="label block mb-1.5">Email address</label>
                <input
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="colleague@company.com"
                  className="input"
                />
              </div>
              
              <div>
                <label className="label block mb-1.5">Role</label>
                <select
                  value={inviteRole}
                  onChange={(e) => setInviteRole(e.target.value)}
                  className="input"
                >
                  <option value="admin">Admin</option>
                  <option value="editor">Editor</option>
                  <option value="viewer">Viewer</option>
                </select>
                <p className="text-xs text-gray-500 mt-1.5">
                  {roleDescriptions[inviteRole]}
                </p>
              </div>
              
              <div className="flex gap-3 pt-2">
                <button 
                  onClick={() => setShowInviteModal(false)}
                  className="btn-secondary flex-1"
                >
                  Cancel
                </button>
                <button 
                  onClick={handleInvite}
                  disabled={isInviting}
                  className="btn-primary flex-1"
                >
                  {isInviting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    'Send Invite'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
